/** @jsx jsx */
import * as React from "react";
import { jsx } from "@emotion/core";
import * as styles from './style';
import AppUtils from "../../../utils/AppUtils";
import TextInput from "../../TextInput";
import CustomHeader, { ICustomHeaderInfo } from "../../utils/CustomHeader";
import { IMicrocycle, formFieldNames, IMicrocycleError } from "../../../containers/FitnessPlan/FitnessTemplate/interface";
import CustomSelect from "../../CustomSelect";
import { FITNESS_GOAL, LEVEL_OPTIONS, WORKOUT_FREQUENCY_OPTIONS, GENDER, EQUIPMENT, WORKOUT_FORMAT } from "../../../containers/FitnessPlan/constants";
import CTA, { CTATheme } from "../../common/CallToAction";


interface IFitnessGoalProps {
    microcycleData: IMicrocycle;
    setMicrocyleData: (microcycleData: IMicrocycle) => void;
    errorStates: IMicrocycleError[];
    handleBackAction: () => void;
    isPatientPlan: boolean;
    isUserPlanPublished: boolean;
    isViewMode: boolean;
    handleEditPlan: () => void;
}

let FitnessGoalHeaderInfos: ICustomHeaderInfo[] = [
    { label: formFieldNames.GOALID, display: "GOAL", mandatory: true },
    { label: formFieldNames.LEVEL, display: "LEVEL", mandatory: true },
    { label: formFieldNames.WORKOUTDAYS, display: "WORKOUT DAYS", mandatory: true },
    { label: formFieldNames.GENDER, display: "GENDER" },
    { label: formFieldNames.REGIMENID, display: "EQUIPMENT" }
];

export const FitnessGoal: React.FC<IFitnessGoalProps> = (props: IFitnessGoalProps) => {
    const { microcycleData: { goalId, description, workoutDays, regimenId, level, meta }, setMicrocyleData, errorStates, handleBackAction, isPatientPlan, isUserPlanPublished, isViewMode, handleEditPlan } = props;


    const getDisplayValue = (options: any[], selectedValue: string | number) => {
        const selectedOption = options.find(({ internalValue }) => internalValue === selectedValue);
        if (selectedOption) {
            return selectedOption.displayValue;
        }
        return null
    }

    const updateGoalData = (value: string, keyName: string) => {
        let currentItem = { ...props.microcycleData, [keyName]: value };
        setMicrocyleData(currentItem);
    }

    const setTemplateName = (event: React.ChangeEvent<HTMLInputElement>) => {
        let currentItem = { ...props.microcycleData, description: event.target.value };
        setMicrocyleData(currentItem);
    }

    const updateMetaData = (value: string, keyName: string) => {
        let currentItem = { ...props.microcycleData, meta: { ...props.microcycleData.meta, [keyName]: value } };
        setMicrocyleData(currentItem);
    }

    const checkForError = (field: string) => {
        if (errorStates) {
            return errorStates.find(({ fieldName }) => fieldName === field) ? true : false
        }
        return false
    }

    return (
        <header css={styles.headerWrapper} >
            <section css={styles.titleContainer}>
                <div css={styles.titleWrapper}>
                    {!isPatientPlan && <img src="/images/back-arrow.svg" alt="back" css={styles.backArrow} onClick={handleBackAction} />}
                    <span css={isPatientPlan ? styles.planTitle : null}>
                        <CustomHeader headerInfos={[{ label: "title", display: "TITLE", mandatory: true }]} rootCls="fitness-template-header" additionalCls={checkForError(formFieldNames.TEMPLATENAME) && "mandatory-error"} />
                        <TextInput name="description" placeholder="Enter Plan Name" value={description} onChange={setTemplateName} customClasses={checkForError(formFieldNames.TEMPLATENAME) ? "fitness-template-name input-error" : "fitness-template-name"} disabled={isViewMode} />
                    </span>
                </div>
                {
                    isViewMode &&
                    <CTA innerText="EDIT" theme={CTATheme.TEXTUAL} onClick={handleEditPlan} />
                }
            </section>
            <section css={styles.planWrapper}>
                <div css={styles.goalPropertyWrapper}>
                    <CustomHeader headerInfos={[FitnessGoalHeaderInfos[0]]} rootCls="fitness-goal-header" additionalCls={checkForError(formFieldNames.GOALID) && "mandatory-error"} />
                    <CustomSelect dropdownValues={FITNESS_GOAL} selectedValue={getDisplayValue(FITNESS_GOAL, goalId)} defaultValue="Select Goal" updateDropdownValue={updateGoalData} keyName={formFieldNames.GOALID} className={checkForError(formFieldNames.GOALID) && "input-error"} disabled={isUserPlanPublished || isViewMode} />
                </div>

                <div css={styles.goalPropertyWrapper}>
                    <CustomHeader headerInfos={[FitnessGoalHeaderInfos[1]]} rootCls="fitness-goal-header" additionalCls={checkForError(formFieldNames.LEVEL) && "mandatory-error"} />
                    <CustomSelect dropdownValues={LEVEL_OPTIONS} selectedValue={getDisplayValue(LEVEL_OPTIONS, level)} defaultValue="Select Level" updateDropdownValue={updateGoalData} keyName={formFieldNames.LEVEL} className={checkForError(formFieldNames.LEVEL) && "input-error"} disabled={isUserPlanPublished || isViewMode} />
                </div>

                <div css={styles.goalPropertyWrapper}>
                    <CustomHeader headerInfos={[FitnessGoalHeaderInfos[2]]} rootCls="fitness-goal-header" additionalCls={checkForError(formFieldNames.WORKOUTDAYS) && "mandatory-error"} />
                    <CustomSelect dropdownValues={WORKOUT_FREQUENCY_OPTIONS} selectedValue={getDisplayValue(WORKOUT_FREQUENCY_OPTIONS, workoutDays)} defaultValue="No. of Days" updateDropdownValue={updateGoalData} keyName={formFieldNames.WORKOUTDAYS} className={checkForError(formFieldNames.WORKOUTDAYS) && "input-error"} disabled={isUserPlanPublished || isViewMode} />
                </div>

                <div css={styles.goalPropertyWrapper}>
                    <CustomHeader headerInfos={[FitnessGoalHeaderInfos[3]]} rootCls="fitness-goal-header" />
                    <CustomSelect dropdownValues={GENDER} selectedValue={getDisplayValue(GENDER, meta && meta.gender)} defaultValue="Select" updateDropdownValue={updateMetaData} keyName={formFieldNames.GENDER} disabled={isUserPlanPublished || isViewMode} />
                </div>

                <div css={styles.goalPropertyWrapper}>
                    <CustomHeader headerInfos={[FitnessGoalHeaderInfos[4]]} rootCls="fitness-goal-header" />
                    <CustomSelect dropdownValues={EQUIPMENT} selectedValue={getDisplayValue(EQUIPMENT, regimenId)} defaultValue="Select Equipment" updateDropdownValue={updateGoalData} keyName={formFieldNames.REGIMENID} disabled={isUserPlanPublished || isViewMode} />
                </div>
            </section>
        </header>
    )
}